import { useState } from "react";
import { toast } from "sonner";
import { p } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Info, Delete, ArrowLeft, KeyRound } from "lucide-react";

const GROUPS = [
  { id: "sala", key: "rs.sala", roles: ["cameriere", "runner", "cassiere", "sommelier"] },
  { id: "direzione", key: "rs.direzione", roles: ["direttore", "titolare"] },
  { id: "reparti", key: "rs.reparti", roles: ["cuoco", "pizzaiolo", "pasticciere", "barista", "manutentore"] },
];

export default function RoleSelect() {
  const { staff, settings, demoMode, support, login } = useApp();
  const { t } = useI18n();
  const [group, setGroup] = useState("sala");
  const [person, setPerson] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const trial = (() => {
    try {
      return JSON.parse(localStorage.getItem("rh_trial_info") || "null");
    } catch {
      return null;
    }
  })();

  const list = staff.filter((s) => {
    const g = GROUPS.find((x) => x.id === group);
    // Chi ha due mansioni compare in entrambi i gruppi: entra dove lavora adesso
    return s.active && (g.roles.includes(s.role) || g.roles.includes(s.alt_role));
  });
  // In che veste entra da questo gruppo: se è la sua seconda mansione si passa `as_role`
  const vesteQui = (s) => {
    const g = GROUPS.find((x) => x.id === group);
    return g.roles.includes(s.role) ? s.role : s.alt_role;
  };

  const submit = async (code, who, veste) => {
    setBusy(true);
    try {
      const u = await p("/staff/login", { username: who.username, pin: code, as_role: veste || who.role });
      login(u);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("rs.badPin"));
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  const tap = (n) => {
    const next = (pin + n).slice(0, 4);
    setPin(next);
    if (next.length === 4 && !busy) submit(next, person, person?.veste);
  };

  return (
    <div className="min-h-screen bg-slate-900 px-4 py-8 text-white" data-testid="role-select-page">
      <div className="mx-auto max-w-3xl">
        <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-500">{t("rs.overline")}</p>
        <div className="mt-2 flex flex-wrap items-end gap-3">
          {settings.logo && <img src={settings.logo} alt="" className="h-12 w-12 rounded-lg bg-white object-contain p-1" data-testid="venue-logo" />}
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{settings.business_name}</h1>
          <p className="pb-1 text-lg font-semibold text-amber-400" data-testid="product-name">
            Reprehendo <span className="text-slate-500">(RH)</span>
          </p>
        </div>
        {settings.motto && settings.motto_on_login !== false && (
          <p className="mt-2 font-head text-base italic text-slate-300 md:text-lg" data-testid="venue-motto">
            «{settings.motto}»
          </p>
        )}

        <div className="mt-4 flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <p className="text-sm text-amber-100">
            <span className="font-bold">{t("beta.tag")}</span> — {demoMode ? t("rs.demoHint") : t("rs.hint")}
          </p>
        </div>

        {trial && (
          <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4" data-testid="trial-banner">
            <p className="text-sm font-bold text-emerald-200">{t("rs.trialReady", { days: trial.days })}</p>
            <p className="mt-1 text-xs text-emerald-100">
              {t("rs.trialBack")} <span className="font-bold">{trial.slug}</span>, {t("rs.trialCode")}{" "}
              <span className="font-num font-bold tracking-[0.15em]">{trial.code}</span> — {t("rs.trialKeep")}
            </p>
          </div>
        )}

        {support && (
          <Card className="mt-4 border-2 border-sky-300 bg-sky-50 p-5" data-testid="assistenza-observe-card">
            <p className="text-sm font-bold text-sky-900">Sei entrato come assistenza</p>
            <p className="mt-1 text-xs text-sky-900/80">
              Il titolare ha autorizzato l'accesso: entri senza chiedere il PIN a nessuno e non
              puoi modificare nulla.
            </p>
            <Button
              className="mt-3 h-11 w-full bg-sky-900 hover:bg-sky-800"
              disabled={busy}
              data-testid="assistenza-observe-btn"
              onClick={async () => {
                setBusy(true);
                try {
                  login(await p("/support/observe"));
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Sessione di assistenza non valida");
                } finally {
                  setBusy(false);
                }
              }}
            >
              Guarda la configurazione (sola lettura)
            </Button>
            <button
              className="mt-2 w-full text-xs font-semibold text-sky-900 underline"
              data-testid="assistenza-esci-btn"
              onClick={() => {
                localStorage.removeItem("rh_tenant_token");
                localStorage.removeItem("rh_staff_session");
                window.location.href = "/rh-console";
              }}
            >
              Esci e torna alla console
            </button>
          </Card>
        )}

        {!person ? (
          <>
            <div className="mt-8 grid grid-cols-3 gap-2">
              {GROUPS.map((g) => (
                <button
                  key={g.id}
                  data-testid={`role-tab-${g.id}`}
                  onClick={() => setGroup(g.id)}
                  className={`rounded-lg border px-3 py-4 text-sm font-semibold transition-colors ${
                    group === g.id ? "border-white bg-white text-slate-900" : "border-slate-700 text-slate-300"
                  }`}
                >
                  {t(g.key)}
                </button>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {list.map((s) => (
                <Card key={s.id} className="flex items-center justify-between gap-3 p-5" data-testid={`staff-card-${s.username}`}>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-900">{s.name}</p>
                    <p className="text-xs text-slate-500">
                      {vesteQui(s) === s.role ? s.role_label : s.alt_role_label}
                    </p>
                    {s.alt_role && (
                      <p className="mt-1 text-[11px] font-semibold text-amber-700" data-testid={`staff-doppio-${s.username}`}>
                        Due mansioni: qui entri come {vesteQui(s) === s.role ? s.role_label : s.alt_role_label}
                      </p>
                    )}
                    {demoMode && s.pin && (
                      <p className="mt-1.5 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold text-amber-800" data-testid={`staff-pin-${s.username}`}>
                        <KeyRound className="h-3 w-3" /> PIN {s.pin}
                      </p>
                    )}
                  </div>
                  <Button
                    data-testid={`enter-as-${s.username}`}
                    className="h-11 shrink-0"
                    disabled={busy}
                    onClick={() => {
                      const veste = vesteQui(s);
                      if (demoMode && s.pin) return submit(s.pin, s, veste);
                      setPerson({ ...s, veste });
                      setPin("");
                    }}
                  >
                    {demoMode && s.pin ? t("rs.enter") : t("rs.enterPin")}
                  </Button>
                </Card>
              ))}
              {list.length === 0 && <p className="text-sm text-slate-400">{t("rs.empty")}</p>}
            </div>

            {support && (
              <Card className="mt-5 border-2 border-sky-300 bg-sky-50 p-5" data-testid="assistenza-observe-card-2">
                <p className="text-sm font-bold text-sky-900">Non ti serve il PIN</p>
                <p className="mt-1 text-xs text-sky-900/80">
                  Usa il tasto azzurro qui sopra: entri come assistenza, in sola lettura.
                </p>
              </Card>
            )}

            {demoMode && (
              <p className="mt-5 text-center text-xs text-slate-400" data-testid="demo-pin-note">
                {t("rs.demoNote")}
              </p>
            )}
          </>
        ) : (
          <Card className="mx-auto mt-8 w-full max-w-xs p-6" data-testid="pin-pad">
            <button className="flex items-center gap-1 text-xs text-slate-500" data-testid="pin-back-btn" onClick={() => setPerson(null)}>
              <ArrowLeft className="h-3.5 w-3.5" /> {t("rs.change")}
            </button>
            <p className="mt-3 text-center font-semibold text-slate-900">{person.name}</p>
            <p className="text-center text-xs text-slate-500">{person.role_label}</p>
            {demoMode && person.pin && (
              <p className="mt-2 text-center text-xs font-bold text-amber-700" data-testid="pin-pad-hint">
                {t("rs.demoPin", { pin: person.pin })}
              </p>
            )}

            <div className="mt-4 flex justify-center gap-3">
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={`pin-dot-${i}`}
                  className={`h-4 w-4 rounded-full ${pin.length > i ? "bg-slate-900" : "bg-slate-200"}`}
                  data-testid={`pin-dot-${i}`}
                />
              ))}
            </div>

            <div className="mt-5 grid grid-cols-3 gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <Button key={n} variant="outline" className="h-14 text-xl font-bold" data-testid={`pin-key-${n}`} onClick={() => tap(String(n))}>
                  {n}
                </Button>
              ))}
              <Button variant="ghost" className="h-14" data-testid="pin-clear-btn" onClick={() => setPin("")}>
                <Delete className="h-5 w-5" />
              </Button>
              <Button variant="outline" className="h-14 text-xl font-bold" data-testid="pin-key-0" onClick={() => tap("0")}>
                0
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
